<template>
  <v-container class="pa-0 pt-2" style="max-width: 1400px !important;">
    <v-row no-gutters>
      <!-- Left sidebar navigation -->
      <v-col cols="12" sm="4" lg="3" class="pr-sm-2">
        <v-card class="sticky-sidebar">
          <v-list class="pt-0">
            <v-list-subheader class="text-h6 font-weight-bold pt-2">
              {{ t('products.categories.title') }}
            </v-list-subheader>
            
            <!-- Search bar -->
            <div class="pa-3">
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="mdi-magnify"
                :label="t('products.search.placeholder')"
                variant="outlined"
                density="compact"
                hide-details
                class="mb-1"
              ></v-text-field>
            </div>
            
            <!-- Category list -->
            <v-list-item
              v-for="category in filteredCategories"
              :key="category.id"
              :active="selectedCategory === category.id"
              @click="selectedCategory = category.id"
              color="primary"
              class="mb-1 category-item"
            >
              <v-list-item-title class="text-subtitle-2">
                {{ category.name[locale] }}
                <span class="text-caption text-grey ml-1">({{ category.products.length }})</span>
              </v-list-item-title>
            </v-list-item>


          </v-list>
        </v-card>
      </v-col>

      <!-- Right product list -->
      <v-col cols="12" sm="8" lg="9">

        <!-- Loading state -->
        <v-row v-if="loading" justify="center" class="py-12">
          <v-col cols="12" class="text-center">
            <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
            <div class="text-h6 text-grey">{{ t('products.states.loading') }}</div>
          </v-col>
        </v-row>

        <!-- Error state -->
        <v-row v-else-if="error" justify="center" class="py-12">
          <v-col cols="12" class="text-center">
            <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
            <div class="text-h6 text-grey mb-2">{{ t('products.states.error') }}</div>
            <div class="text-body-2 text-grey">{{ error }}</div>
            <v-btn color="primary" class="mt-4" @click="loadData">{{ t('products.actions.retry') }}</v-btn>
          </v-col>
        </v-row>

        <!-- Product list -->
        <v-row v-else-if="filteredCategories.length" class="mt-0 bg-white ml-2" align="stretch">
          <v-col
            v-for="product in filteredProducts"
            :key="product.id"
            cols="12"
            sm="6"
            md="3"
            lg="3"
            xl="3"
            class="pa-2 d-flex"
          >
            <product-card
              :product="product"
              :locale="locale"
            />
          </v-col>
        </v-row>

        <!-- No data prompt -->
        <v-row v-else justify="center" class="py-12">
          <v-col cols="12" class="text-center">
            <v-icon size="64" color="grey" class="mb-4">mdi-tractor-variant</v-icon>
            <div class="text-h6 text-grey">{{ t('products.states.noData') }}</div>
          </v-col>
        </v-row>

          <!-- Pagination and refresh controls -->
        <div class="d-flex justify-end mb-2" v-if='false'>
         
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            density="comfortable"
          ></v-pagination>
        </div>

      </v-col>
    </v-row>



  </v-container>

  <!-- Footer -->

</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import type { ProductCategory } from '@/types/product';
import { loadProductData } from '@/services/productService';
import ProductCard from '@/components/ProductCard.vue';

const { t, locale } = useI18n();
const route = useRoute();

// 产品数据
const categories = ref<ProductCategory[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// 搜索和筛选
const searchQuery = ref('');
const selectedCategory = ref<string | null>(null);
// Pagination placeholders (template has a disabled block referencing these)
const currentPage = ref(1);
const totalPages = ref(1);

// 加载数据
const loadData = async () => {
  try {
    loading.value = true;
    categories.value = await loadProductData();
    
    // 优先使用路由查询参数，否则选择第一个分类
    const categoryFromQuery = route.query.category as string;
    if (categoryFromQuery) {
      const matchedCategory = categories.value.find(c => c.id === categoryFromQuery);
      selectedCategory.value = matchedCategory ? matchedCategory.id : null;
    } else if (categories.value.length > 0) {
      selectedCategory.value = categories.value[0].id;
    }
  } catch (err) {
    error.value = t('products.errors.loadFailed');
    console.error('Failed to load products:', err);
  } finally {
    loading.value = false;
  }
};

// 监听路由变化，更新选中的分类
watch(() => route.query.category, (newCategory) => {
  if (categories.value.length > 0) {
    const matchedCategory = categories.value.find(c => c.id === newCategory);
    selectedCategory.value = matchedCategory ? matchedCategory.id : null;
  }
}, { immediate: true });

// 过滤分类列表（只用于显示）
const filteredCategories = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return categories.value.filter(category => {
    // 搜索匹配
    if (!query) return true;
    
    // 匹配分类名称
    if (category.name[locale.value].toLowerCase().includes(query)) {
      return true;
    }
    
    // 匹配产品名称和描述
    return category.products.some(p => 
      p.name[locale.value].toLowerCase().includes(query) ||
      p.model.toLowerCase().includes(query) ||
      p.description[locale.value].toLowerCase().includes(query)
    );
  });
});

// 过滤产品列表（用于显示产品）
const filteredProducts = computed(() => {
  const query = searchQuery.value.toLowerCase();
  const selectedCat = categories.value.find(c => c.id === selectedCategory.value);
  
  if (!selectedCat) return [];
  
  return selectedCat.products.filter(product => {
    if (!query) return true;
    
    return (
      product.name[locale.value].toLowerCase().includes(query) ||
      product.model.toLowerCase().includes(query) ||
      product.description[locale.value].toLowerCase().includes(query)
    );
  });
});

// 初始化
onMounted(loadData);
</script>

<style scoped>
.sticky-sidebar {
  position: sticky;
  top: 0;
  background-color: #f5f5f5;
  border-radius: 8px 8px 0 0;
}

.max-width-300 {
  max-width: 300px;
}

.category-item {
  border-radius: 0;
  margin: 2px 0;
  padding-left: 16px;
  transition: all 0.3s ease;
}

.category-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.category-item.v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.1);
  border-left: 3px solid rgb(var(--v-theme-primary));
  font-weight: 600;
}

/* Allow long category names to wrap instead of truncating */
.category-item .v-list-item-title {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .sticky-sidebar {
    position: static;
  }
  
  .max-width-300 {
    max-width: none;
  }
}
</style>
